import { useState } from 'react';
import {
  makeStyles,
  Card,
  CardContent,
  CardMedia,
  Box,
} from '@material-ui/core';
import Typography from '../../overrides/Typography';
import CancelLabel from '../../core/Chips/Label';
import CalendarSvg from '../../../assets/icons/calendar.svg';
import TimerSvg from '../../../assets/icons/timer.svg';
import ProfileSvg from '../../../assets/icons/profile.svg';
import { ReactComponent as BenefitsIcon } from '../../../assets/icons/grv-benefits-small.svg';
import createInflection from '../../../utils/inflection/createInflection';
import MediaImg from '../../../assets/images/media.png';
import {
  getFormattedDate,
  getTypeFromReservation,
  getScreenReaderFormattedDate,
} from '../../../utils/reservationFilters';
import {
  CANCELED_LABEL,
  GUEST_INFLECTION,
  RESERVATIONS_DETAILS_LABEL,
  EXCLUSIVE_TABLE_TEXT,
} from '../../../assets/copy';

import { generateScreenReaderText } from '../../../utils/stringHelperMethods';

import EllipsisText from '../EllipsisText';
import { useViewport } from '../../../utils/useViewport';
import { WIDTH_BREAKPOINT } from '../../../utils/constants/Breakpoints';
import { cardHoverStyles } from '../../../utils/constants/hoverStyles';

const useStyles = makeStyles((theme) => ({
  card: ({ isPastCard, hoverable }) => ({
    display: 'flex',
    height: isPastCard ? 'min-content' : 118,
    width: '100%',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2),
    },
    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.2)',
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    ...(!isPastCard && {
      [theme.breakpoints.up('xl')]: {
        height: 162,
      },
    }),
    ...(hoverable && cardHoverStyles),
  }),
  content: ({ isPastCard }) => {
    const columnAlign = {
      alignItems: 'flex-start',
      flexDirection: 'column',
    };
    return {
      flex: '1 0 auto',
      display: 'flex',
      placeContent: 'space-between',
      ...(isPastCard
        ? {
            boxSizing: 'border-box',
            height: '100%',
            margin: theme.spacing(0, 1),
          }
        : {
            boxSizing: 'border-box',
            height: '100%',
            alignItems: 'center',
            [theme.breakpoints.up('xl')]: columnAlign,
          }),
    };
  },
  insider: {
    width: '16px',
    height: '16px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25),
    '& path': {
      fill: `${theme.palette.yellow[40]} !important`,
    },
  },
  cardholderLabel: {
    color: theme.palette.secondary['65'],
    marginLeft: theme.spacing(0.25),
  },
  cover: {
    width: 118,
    height: '100%',
  },
  image: {
    marginRight: theme.spacing(2),
  },
  cardContentBox: {
    width: '100%',
    overflow: 'hidden',
  },
  benefitsIconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  topLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
}));

export default function SmallCard({
  reservation,
  venue,
  label,
  type = getTypeFromReservation(reservation), // Other types are Fill or Cancel (from UpdateReservation)
  hoverable = false,
  hideImage = false,
}) {
  const isPastCard = type === 'Canceled' || type === 'Completed';
  const classes = useStyles({
    isPastCard,
    hoverable,
  });
  const [cardVenue] = useState(reservation.venue || venue);

  const generateReservationText = () => {
    return generateScreenReaderText(
      ',',
      RESERVATIONS_DETAILS_LABEL,
      reservation.venue.name,
      getScreenReaderFormattedDate(reservation, type),
      reservation.timeSlot || reservation.time,
      createInflection(GUEST_INFLECTION, reservation.guests)
    );
  };

  const { width } = useViewport();

  const exclusiveTable = (isInline) => (
    <>
      <BenefitsIcon
        className={classes.insider}
        style={{marginLeft: isInline ? 8 : 0}}
        role="presentation"
      />
      <Typography variant="tiny1Normal" className={classes.cardholderLabel}>
        {EXCLUSIVE_TABLE_TEXT}
      </Typography>
    </>
  );

  return (
    <Box
      role={`${type === 'Completed' ? 'link' : 'none'}`}
      aria-label={`${label ? label : ''} ${generateReservationText()},
      ${type === 'Canceled' ? CANCELED_LABEL : ''} `}>
      <Card className={classes.card} aria-hidden="true">
        {!hideImage && (
          <Box>
            <CardMedia
              component={'img'}
              className={classes.cover}
              image={cardVenue.images[0] || MediaImg}
              alt="venue image"
            />
          </Box>
        )}
        <Box overflow={'hidden'} width="100%">
          <CardContent className={classes.content}>
            <Box className={classes.cardContentBox}>
              <Box className={classes.topLine}>
                <EllipsisText
                  variant="medium1Semibold"
                  maxWidth={'50%'}
                  text={cardVenue.name}
                />
                {type === 'Canceled' && (
                  <Box paddingLeft={'8px'}>
                    <CancelLabel text={CANCELED_LABEL} />
                  </Box>
                )}
              </Box>
              <Box display="flex">
                <img src={CalendarSvg} alt="" className={classes.image} />
                <Typography variant="small2Normal">
                  {getFormattedDate(reservation, type)}
                </Typography>
              </Box>
              <Box display="flex">
                <img src={TimerSvg} alt="" className={classes.image} />
                <Typography variant="small2Normal">
                  {reservation.timeSlot || reservation.time}
                </Typography>
                {isPastCard &&
                  (reservation.isCardholderTable ||
                    reservation.isExclusive) && (
                    <Box className={classes.benefitsIconContainer}>
                      <BenefitsIcon
                        className={classes.insider}
                        role="presentation"
                      />
                      <Typography
                        variant="tiny1Normal"
                        className={classes.cardholderLabel}>
                        {EXCLUSIVE_TABLE_TEXT}
                      </Typography>
                    </Box>
                  )}
                {!isPastCard &&
                  ((width >= 420 && width < WIDTH_BREAKPOINT) ||
                    width >= 1200) &&
                  (reservation.isCardholderTable || reservation.isExclusive) &&
                  exclusiveTable(true)}
              </Box>
              <Box display="flex">
                <img src={ProfileSvg} alt="" className={classes.image} />
                <Typography variant="small2Normal">
                  {createInflection(GUEST_INFLECTION, reservation.guests)}
                </Typography>
              </Box>
              <Box display="flex">
                {!isPastCard &&
                  (width < 420 ||
                    (width >= WIDTH_BREAKPOINT && width < 1200)) &&
                  (reservation.isCardholderTable || reservation.isExclusive) &&
                  exclusiveTable(false)}
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
}
